import React from 'react';
import { ChevronDownIcon, RotateCcwIcon } from 'lucide-react';
import { Assumptions } from '../types/compensation';
import { NumberField } from './NumberField';
import { TextField } from './TextField';
interface AssumptionsPanelProps {
  assumptions: Assumptions;
  onChange: (patch: Partial<Assumptions>) => void;
  onReset: () => void;
}
export function AssumptionsPanel({
  assumptions,
  onChange,
  onReset
}: AssumptionsPanelProps) {
  const [open, setOpen] = React.useState(false);
  const a = assumptions;
  return <section className="rounded-card border border-line bg-ink-900" data-id="element-1" data-mp="vmvi6jrc0mu9">
      <button type="button" onClick={() => setOpen(v => !v)} aria-expanded={open} aria-controls="assumptions-body" className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left lg:px-8" data-id="element-2" data-mp="19fl4kparvtrw">
        <span data-id="element-3" data-mp="1jdiqv792nghv">
          <span className="block text-[13px] font-semibold uppercase tracking-[0.2em] text-paper" data-id="element-4" data-mp="1jawkhaxx3718">
            Model assumptions
          </span>
          <span className="mt-1 block text-sm text-paper-dim" data-id="element-5" data-mp="2x2naootrrcpc">
            Every input below is editable — the whole model recalculates live
          </span>
        </span>
        <ChevronDownIcon className={['h-5 w-5 shrink-0 text-paper-muted transition-transform duration-200 ease-out', open ? 'rotate-180' : ''].join(' ')} aria-hidden="true" data-id="element-6" data-mp="3709wlv9hk4it" />
      </button>

      {open ? <div id="assumptions-body" className="border-t border-line px-6 py-7 lg:px-8" data-id="element-7" data-mp="3fylumicom4la">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-3" data-id="element-8" data-mp="3pqj6lrbil0l2">
            <fieldset data-id="element-9" data-mp="3prb457oo98dz">
              <legend className="text-xs uppercase tracking-[0.16em] text-paper-muted" data-id="element-10" data-mp="3psvpc6p3jez6">
                Salary basis
              </legend>
              <div className="mt-4 space-y-4" data-id="element-11" data-mp="2ml9ce1oxnnv">
                <NumberField label="Reference market salary" value={a.referenceMonthlySalary} step={1000} suffix="DKK / month" onChange={v => onChange({
              referenceMonthlySalary: v
            })} data-id="element-12" data-mp="2jqoilerm0tx" />
                <NumberField label="Minimum fixed salary" value={a.minFixedSalary} step={1000} suffix="DKK / month" onChange={v => onChange({
              minFixedSalary: v
            })} data-id="element-13" data-mp="1zkcyd8fiquoj" />
                <NumberField label="Maximum fixed salary" value={a.maxFixedSalary} step={1000} suffix="DKK / month" onChange={v => onChange({
              maxFixedSalary: v
            })} data-id="element-14" data-mp="2napcj11cxnnk" />
                <div className="grid grid-cols-2 gap-3" data-id="element-15" data-mp="2rddovipa5cpa">
                  <NumberField label="Commission multiplier" value={a.commissionMultiplier} step={0.5} suffix="×" onChange={v => onChange({
                commissionMultiplier: v
              })} data-id="element-16" data-mp="2rbsubt2yttrf" />
                  <NumberField label="Equity multiplier" value={a.equityMultiplier} step={0.5} suffix="×" onChange={v => onChange({
                equityMultiplier: v
              })} data-id="element-17" data-mp="38p13ieo2p059" />
                </div>
              </div>
            </fieldset>

            <fieldset data-id="element-18" data-mp="2pvx7imndub7h">
              <legend className="text-xs uppercase tracking-[0.16em] text-paper-muted" data-id="element-19" data-mp="2pvf4962aywq2">
                Commission &amp; targets
              </legend>
              <div className="mt-4 space-y-4" data-id="element-20" data-mp="2gvryk82jj4r7">
                <div className="grid grid-cols-2 gap-3" data-id="element-21" data-mp="2gx3e155cm0y5">
                  <NumberField label="Existing min %" value={a.existingRateMin} step={0.5} suffix="%" onChange={v => onChange({
                existingRateMin: v
              })} data-id="element-22" data-mp="2kxdfc4i7436o" />
                  <NumberField label="Existing max %" value={a.existingRateMax} step={0.5} suffix="%" onChange={v => onChange({
                existingRateMax: v
              })} data-id="element-23" data-mp="j3wcrf8x9sis" />
                </div>
                <div className="grid grid-cols-2 gap-3" data-id="element-24" data-mp="3gkq2trq8nxg8">
                  <NumberField label="New business min %" value={a.newRateMin} step={0.5} suffix="%" onChange={v => onChange({
                newRateMin: v
              })} data-id="element-25" data-mp="3bo9b6njtz0ye" />
                  <NumberField label="New business max %" value={a.newRateMax} step={0.5} suffix="%" onChange={v => onChange({
                newRateMax: v
              })} data-id="element-26" data-mp="2xw2xd845h624" />
                </div>
                <NumberField label="Existing incremental revenue target" value={a.existingRevenueTarget} step={250_000} suffix="DKK / year" onChange={v => onChange({
              existingRevenueTarget: v
            })} data-id="element-27" data-mp="2nzq2i6wvcm5s" />
                <NumberField label="New business target" value={a.newBusinessTarget} step={250_000} suffix="DKK / year" onChange={v => onChange({
              newBusinessTarget: v
            })} data-id="element-28" data-mp="18exxcj4901iy" />
              </div>
            </fieldset>

            <fieldset data-id="element-29" data-mp="kotvohamebul">
              <legend className="text-xs uppercase tracking-[0.16em] text-paper-muted" data-id="element-30" data-mp="kqeljtlzp6m4">
                Ownership &amp; vesting
              </legend>
              <div className="mt-4 space-y-4" data-id="element-31" data-mp="uf6bjw7hpgue">
                <NumberField label="Company valuation" value={a.companyValuation} step={1_000_000} suffix="DKK" onChange={v => onChange({
              companyValuation: v
            })} data-id="element-32" data-mp="ufy8pbgqjmms" />
                <div className="grid grid-cols-2 gap-3" data-id="element-33" data-mp="3q31z97nzyj3g">
                  <NumberField label="Vesting period" value={a.vestingYears} step={1} suffix="years" onChange={v => onChange({
                vestingYears: v
              })} data-id="element-34" data-mp="3q1h69mhbz87p" />
                  <NumberField label="Cliff" value={a.cliffMonths} step={1} suffix="months" onChange={v => onChange({
                cliffMonths: v
              })} data-id="element-35" data-mp="3uvlee92c1mad" />
                </div>
                <NumberField label="Vested at cliff" value={a.firstYearVestPercent} step={5} suffix="%" onChange={v => onChange({
              firstYearVestPercent: v
            })} data-id="element-36" data-mp="3h35hzr3xsmif" />

                <div className="border-t border-line-soft pt-4" data-id="element-37" data-mp="rdk13ke3jjct">
                  <p className="text-[11px] uppercase tracking-[0.14em] text-paper-dim" data-id="element-38" data-mp="rdbm9myey3yz">
                    Milestone warrant grants
                  </p>
                  <div className="mt-3 space-y-3" data-id="element-39" data-mp="2easz7baaoh9e">
                    {a.milestones.map(m => <TextField key={m.id} label={m.label} value={m.grant} placeholder="To be agreed" onChange={value => onChange({
                  milestones: a.milestones.map(item => item.id === m.id ? {
                    ...item,
                    grant: value
                  } : item)
                })} data-id="element-40" data-mp="20b1rttea9z4d" />)}
                  </div>
                </div>
              </div>
            </fieldset>
          </div>

          <div className="mt-8 border-t border-line-soft pt-5" data-id="element-41" data-mp="3bw71bweoa69f">
            <button type="button" onClick={onReset} className="inline-flex items-center gap-2 rounded-lg border border-line px-4 py-2 text-sm text-paper-muted transition-colors duration-150 ease-out hover:border-paper-dim hover:text-paper" data-id="element-42" data-mp="3gqbb0nfbose7">
              <RotateCcwIcon className="h-4 w-4" aria-hidden="true" data-id="element-43" data-mp="16xh1na4w3hba" />
              Reset to Snakker defaults
            </button>
          </div>
        </div> : null}
    </section>;
}